import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useI18nText } from '@/lib/utils/i18nText';
import Tooltip from '@/components/Tooltip';
import { getUncheckedAdvertiserActionCounts } from '@/lib/api/campaign.api';
import { useAppSelector } from '@/store/hooks';
import { usePreviewMode } from '@/contexts/PreviewModeContext';

export function CampaignNavBox({
  campaignId,
  campaignStatus,
}: {
  campaignId: string;
  campaignStatus?: string;
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(true);
  const { pathname } = useLocation();
  const currentUser = useAppSelector((s) => s.envs.currentUser);
  const isPreviewMode = usePreviewMode();
  const isAdmin = currentUser?.role === 'admin' && !isPreviewMode;

  /* ── 배지 카운트 ── */
  const [candidatesBadge, setCandidatesBadge] = useState(0);
  const [progressBadge, setProgressBadge] = useState(0);

  // admin 전용 API — 게스트/광고주 세션에서는 403이므로 호출하지 않음
  useEffect(() => {
    if (!campaignId || !isAdmin) return;
    getUncheckedAdvertiserActionCounts([campaignId])
      .then((counts) => {
        const unchecked = counts[campaignId] ?? {};
        setCandidatesBadge(unchecked.setCandidateManualEstimation || 0);
        setProgressBadge(unchecked.changeVideoStatus || 0);
      })
      .catch(() => {});
  }, [campaignId, isAdmin]);

  const disabledReasonByStatus: Record<string, string> = {
    draft:         t('Not available for draft campaigns',    '초안 캠페인에서는 사용할 수 없습니다'),
    private_draft: t('Not available for draft campaigns',    '초안 캠페인에서는 사용할 수 없습니다'),
    template:      t('Not available for template campaigns', '템플릿 캠페인에서는 사용할 수 없습니다'),
    roster:        t('Not available for roster campaigns',   '로스터 캠페인에서는 사용할 수 없습니다'),
  };
  const disabledReason = campaignStatus ? disabledReasonByStatus[campaignStatus] : undefined;

  const navItems: {
    to: string;
    icon: string;
    btnClass: string;
    label: string;
    badge?: number;
    badgeTooltip?: string;
    disabled?: string;
    forceActive?: boolean;
    dividerBefore?: boolean;
  }[] = [
    {
      to: `/dashboard/campaign/${campaignId}`,
      icon: 'fa-eye',
      btnClass: 'action-btn--view',
      label: t('Campaign details', '캠페인 상세'),
    },
    {
      to: `/candidates/${campaignId}`,
      icon: 'fa-user-plus',
      btnClass: 'action-btn--candidates',
      label: t('Campaign candidates', '캠페인 후보'),
      badge: candidatesBadge,
      badgeTooltip: t('This number shows candidates awaiting estimation', '이 숫자는 견적 입력 대기 중인 후보의 수입니다'),
    },
    {
      to: `/communications/${campaignId}`,
      icon: 'fa-running',
      btnClass: 'action-btn--progress',
      label: t('Campaign progress', '캠페인 프로그레스'),
      badge: progressBadge,
      badgeTooltip: t(
        'Posts with status changes awaiting your review. Open the Content Creation or Waiting for release tab and check the approval status in each row\'s POSTS column.',
        '상태 변경으로 검토가 필요한 게시물 수입니다. Content Creation 또는 Waiting for release 탭을 열어 각 행의 POSTS 컬럼에서 승인 여부를 확인하세요.',
      ),
    },
    {
      to: `/stats/campaign/${campaignId}`,
      icon: 'fa-line-chart',
      btnClass: 'action-btn--analysis',
      label: t('Analytics & summary', '캠페인 분석 & 요약'),
      disabled: disabledReason,
      forceActive:
        pathname.startsWith(`/analytics/campaign/${campaignId}`) ||
        pathname.startsWith(`/stats/campaign/${campaignId}`) ||
        pathname.startsWith(`/summary/${campaignId}`),
    },
    {
      to: `/payments/reviews/${campaignId}`,
      icon: 'fa-dollar',
      btnClass: 'action-btn--payments',
      label: t('Payments reviews', '결제 검토'),
      disabled: disabledReason,
    },
    {
      to: `/campaigns/advertiser/${campaignId}`,
      icon: 'fa-users',
      btnClass: 'action-btn--potential',
      label: t('Advertiser info', '광고주 정보'),
      dividerBefore: true,
      forceActive: pathname.startsWith(`/campaigns/advertiser/${campaignId}`),
    },
    {
      to: `/campaigns/edit/${campaignId}`,
      icon: 'fa-pencil-square-o',
      btnClass: 'action-btn--edit',
      label: t('Campaign edit', '캠페인 편집'),
    },
  ];

  return (
    <div className="filter-section campaign-status-filter">
      <div className="campaign-stats-toggle">
        <i className="fa fa-sitemap campaign-stats-toggle-icon" />
        <span>{t('Navigation', '네비게이션')}</span>
        <Tooltip content={t('Click to expand or collapse', '클릭하면 펼치기/접기가 가능합니다')}>
          <button type="button" className="campaign-stats-toggle-caret-btn" onClick={() => setOpen((v) => !v)}>
            <i className={`fa fa-caret-${open ? 'up' : 'down'}`} />
          </button>
        </Tooltip>
      </div>
      {open && (
        <nav className="campaign-stats-body campaign-ctx-nav">
          {navItems.map(({ to, icon, btnClass, label, badge, badgeTooltip, disabled, forceActive, dividerBefore }) => {
            const badgeEl = !!badge && badge > 0 && (
              <Tooltip essential content={badgeTooltip ?? ''}>
                <span className="action-btn-badge" style={{ top: -8, right: -8, cursor: 'default' }}>
                  {badge > 99 ? '99+' : badge}
                </span>
              </Tooltip>
            );
            const iconEl = (
              <span className="action-btn-wrap campaign-ctx-sidebar__item-icon" style={{ flexShrink: 0 }}>
                <span
                  className={`action-btn ${btnClass} flex justify-center items-center w-[26px] h-[26px]`}
                >
                  <i className={`fa ${icon}`} />
                </span>
                {badgeEl}
              </span>
            );
            return (
              <div key={to}>
                {dividerBefore && <hr className="mx-3 my-2 border-dashed border-ggc-border-gray" />}
                {disabled ? (
                  <Tooltip content={disabled}>
                    <span className="campaign-ctx-sidebar__item campaign-ctx-sidebar__item--disabled">
                      {iconEl}
                      <span className="campaign-ctx-sidebar__item-label">{label}</span>
                    </span>
                  </Tooltip>
                ) : (
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      `campaign-ctx-sidebar__item${(isActive || forceActive) ? ' campaign-ctx-sidebar__item--active' : ''}`
                    }
                  >
                    {iconEl}
                    <span className="campaign-ctx-sidebar__item-label">{label}</span>
                  </NavLink>
                )}
              </div>
            );
          })}
        </nav>
      )}
    </div>
  );
}
